import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'
import { FiDownload, FiRefreshCw } from 'react-icons/fi'
import api from '../../utils/api'
import { exportarResumoPdf } from '../../utils/resumoPdf'

/**
 * Consulta rápida dos números do evento, no próprio balcão.
 *
 * Existe porque a pergunta chega ao balcão, não ao admin: a organização passa e pergunta
 * quanta gente entrou hoje, e o operador não pode abrir outra ferramenta no meio da fila.
 *
 * Cada bloco separa PESSOAS de LEITURAS. Sem controle de saída a mesma pessoa lê o crachá
 * várias vezes no dia, e reimpressão de etiqueta grava check-in novo — somar registros
 * responderia "quantas passagens houve", que é outro número e outra pergunta.
 */

interface DiaAcesso {
  dia: string
  pessoas: number
  leituras: number
  salas: number
}

interface DiaCheckin {
  dia: string
  pessoas: number
  retiradas: number
}

interface Origem {
  estados: Array<{ uf: string; nome: string; total: number }>
  nacionalidades: Array<{ valor: string; total: number }>
  semEstado: number
  totalPessoas: number
}

const diaLegivel = (ymd: string) => {
  const [a, m, d] = ymd.split('-')
  if (!a || !m || !d) return ymd
  const data = new Date(Number(a), Number(m) - 1, Number(d))
  const semana = data.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  return `${d}/${m} · ${semana}`
}

/** O dia de hoje no fuso do evento, para destacar a linha que o balcão está vivendo. */
const hojeNoEvento = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date())

const Painel: React.FC<{
  titulo: string
  descricao: string
  carregando: boolean
  children: React.ReactNode
}> = ({ titulo, descricao, carregando, children }) => (
  <Box
    borderWidth="1px"
    borderColor={useColorModeValue('gray.200', 'gray.700')}
    borderRadius="lg"
    bg={useColorModeValue('white', 'gray.800')}
    p={5}
  >
    <Heading size="sm" mb={1}>
      {titulo}
    </Heading>
    <Text fontSize="xs" color="gray.500" mb={4}>
      {descricao}
    </Text>
    {carregando ? <Skeleton height="140px" borderRadius="md" /> : children}
  </Box>
)

const ResumoPanel: React.FC = () => {
  const [acessos, setAcessos] = useState<DiaAcesso[]>([])
  const [checkins, setCheckins] = useState<DiaCheckin[]>([])
  const [origem, setOrigem] = useState<Origem | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [nomeEvento, setNomeEvento] = useState<string | undefined>(undefined)

  const buscar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const [a, c, o] = await Promise.all([
        api.get('/access/por-dia'),
        api.get('/checkin/por-dia'),
        api.get('/users/por-origem'),
      ])
      setAcessos(a.data || [])
      setCheckins(c.data || [])
      setOrigem(o.data || null)
      // O nome do evento so serve ao cabecalho do PDF; se falhar, o relatorio sai sem ele.
      try {
        const ev = await api.get('/events')
        const lista = Array.isArray(ev.data) ? ev.data : []
        setNomeEvento(lista[0]?.name || lista[0]?.shortName)
      } catch {
        setNomeEvento(undefined)
      }
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível carregar os números.')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    buscar()
  }, [buscar])

  // Cores resolvidas SEMPRE, fora do bloco condicional: hook chamado às vezes muda a ordem
  // entre renders, que é o que a regra dos hooks proíbe.
  const fundoAcesso = useColorModeValue('teal.50', 'teal.900')
  const fundoCheckin = useColorModeValue('purple.50', 'purple.900')

  const [gerandoPdf, setGerandoPdf] = useState(false)

  /**
   * PDF com o que esta na tela, incluindo as ressalvas.
   *
   * Um numero solto num papel circula sem quem o explique: "pessoas" e "leituras" vao
   * impressas com o que significam, senao a soma dos dias vira "participantes do evento"
   * na mao de quem so recebeu o arquivo.
   */
  const baixarPdf = async () => {
    setGerandoPdf(true)
    try {
      await exportarResumoPdf({ acessos, checkins, origem, nomeEvento })
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível gerar o PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  const hoje = hojeNoEvento()
  const acessoHoje = acessos.find(d => d.dia === hoje)
  const checkinHoje = checkins.find(d => d.dia === hoje)

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Box>
          <Heading size="md">Resumo do evento</Heading>
          <Text fontSize="sm" color="gray.500">
            Números para a organização, atualizados na hora.
          </Text>
        </Box>
        <Flex gap={2}>
          <Button
            leftIcon={<Icon as={FiRefreshCw} />}
            size="sm"
            onClick={buscar}
            isLoading={carregando}
            variant="outline"
          >
            Atualizar
          </Button>
          <Button
            leftIcon={<Icon as={FiDownload} />}
            size="sm"
            colorScheme="blue"
            onClick={baixarPdf}
            isLoading={gerandoPdf}
            loadingText="Gerando…"
            isDisabled={carregando || (acessos.length === 0 && checkins.length === 0)}
          >
            Exportar PDF
          </Button>
        </Flex>
      </Flex>

      {erro && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {erro}
        </Alert>
      )}

      {/* O dia de hoje em destaque: é o que o balcão é perguntado o tempo todo. */}
      {!carregando && (acessoHoje || checkinHoje) && (
        <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4} mb={5}>
          <Box borderWidth="1px" borderRadius="lg" p={4} bg={fundoAcesso}>
            <Text fontSize="xs" color="gray.600" fontWeight="700">
              ENTRARAM NAS SALAS HOJE
            </Text>
            <Flex align="baseline" gap={2}>
              <Text fontSize="3xl" fontWeight="800" color="teal.600" lineHeight="1.1">
                {acessoHoje?.pessoas ?? 0}
              </Text>
              <Text fontSize="sm" color="gray.600">
                pessoas · {acessoHoje?.leituras ?? 0} leituras
              </Text>
            </Flex>
          </Box>
          <Box borderWidth="1px" borderRadius="lg" p={4} bg={fundoCheckin}>
            <Text fontSize="xs" color="gray.600" fontWeight="700">
              RETIRARAM CREDENCIAL HOJE
            </Text>
            <Flex align="baseline" gap={2}>
              <Text fontSize="3xl" fontWeight="800" color="purple.600" lineHeight="1.1">
                {checkinHoje?.pessoas ?? 0}
              </Text>
              <Text fontSize="sm" color="gray.600">
                pessoas · {checkinHoje?.retiradas ?? 0} impressões
              </Text>
            </Flex>
          </Box>
        </SimpleGrid>
      )}

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={5}>
        <Painel
          titulo="Público nas palestras, por dia"
          descricao="Pessoas distintas que entraram em alguma sala"
          carregando={carregando}
        >
          {acessos.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              Nenhum acesso registrado.
            </Text>
          ) : (
            <>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Dia</Th>
                    <Th isNumeric>Pessoas</Th>
                    <Th isNumeric>Leituras</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {acessos.map(d => (
                    <Tr key={d.dia} bg={d.dia === hoje ? 'teal.50' : undefined}>
                      <Td>
                        {diaLegivel(d.dia)}
                        {d.dia === hoje && (
                          <Badge ml={2} colorScheme="teal" fontSize="0.6rem">
                            hoje
                          </Badge>
                        )}
                      </Td>
                      <Td isNumeric fontWeight="bold">
                        {d.pessoas}
                      </Td>
                      <Td isNumeric color="gray.500">
                        {d.leituras}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {acessos.length > 1 && (
                <Text fontSize="xs" color="gray.500" mt={3}>
                  Quem foi em mais de um dia conta em cada um — a soma não é o público do
                  evento.
                </Text>
              )}
            </>
          )}
        </Painel>

        <Painel
          titulo="Credenciamento por dia"
          descricao="Pessoas que retiraram credencial no balcão ou no totem"
          carregando={carregando}
        >
          {checkins.length === 0 ? (
            <Text fontSize="sm" color="gray.500">
              Nenhum check-in registrado.
            </Text>
          ) : (
            <>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Dia</Th>
                    <Th isNumeric>Pessoas</Th>
                    <Th isNumeric>Impressão sem checkin</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {checkins.map(d => (
                    <Tr key={d.dia} bg={d.dia === hoje ? 'purple.50' : undefined}>
                      <Td>
                        {diaLegivel(d.dia)}
                        {d.dia === hoje && (
                          <Badge ml={2} colorScheme="purple" fontSize="0.6rem">
                            hoje
                          </Badge>
                        )}
                      </Td>
                      <Td isNumeric fontWeight="bold">
                        {d.pessoas}
                      </Td>
                      <Td isNumeric color="gray.500">
                        {d.retiradas}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              <Text fontSize="xs" color="gray.500" mt={3}>
                A coluna conta todas as emissões do dia, inclusive reimpressão da mesma
                pessoa — por isso é maior que "Pessoas".
              </Text>
            </>
          )}
        </Painel>

        <Painel
          titulo="De onde vem o público"
          descricao="Estado declarado na inscrição"
          carregando={carregando}
        >
          {!origem ? null : (
            <>
              <Flex align="baseline" gap={2} mb={3}>
                <Text fontSize="3xl" fontWeight="800" lineHeight="1" color="teal.600">
                  {origem.estados.length}
                </Text>
                <Text fontSize="sm" color="gray.600" fontWeight="600">
                  {origem.estados.length === 1 ? 'estado' : 'estados'}
                </Text>
              </Flex>

              <Box maxH="240px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Tbody>
                    {origem.estados.map(e => (
                      <Tr key={e.uf}>
                        <Td py={1}>
                          <Badge colorScheme="teal" variant="subtle" mr={2}>
                            {e.uf}
                          </Badge>
                          <Text as="span" fontSize="sm" color="gray.600">
                            {e.nome}
                          </Text>
                        </Td>
                        <Td py={1} isNumeric fontWeight="600">
                          {e.total}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {origem.semEstado > 0 && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {origem.semEstado} sem estado informado.
                </Text>
              )}

              {/*
                País não é campo da inscrição. Mostrar a nacionalidade declarada e dizer
                isso é mais útil que devolver um número deduzido de domínio de e-mail.
              */}
              <Box mt={4} pt={3} borderTopWidth="1px" borderColor="gray.200">
                <Text fontSize="xs" fontWeight="700" color="gray.600" mb={1}>
                  NACIONALIDADE
                </Text>
                {origem.nacionalidades.map(n => (
                  <Flex key={n.valor} justify="space-between" fontSize="sm">
                    <Text color="gray.600" textTransform="capitalize">
                      {n.valor}
                    </Text>
                    <Text fontWeight="600">{n.total}</Text>
                  </Flex>
                ))}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  O país não é perguntado na inscrição — só se a pessoa é brasileira ou
                  estrangeira.
                </Text>
              </Box>
            </>
          )}
        </Painel>
      </SimpleGrid>
    </Box>
  )
}

export default ResumoPanel
